import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Pagination from "@material-ui/lab/Pagination";
import styled from "styled-components";
import { fetchDonors } from "./fundraisingSlice";
import { formatDate } from "helper/dateFormat";
import { numberFormat } from "helper/numberFormat";
import RightDrawer from "components/drawer";
import FinanceDrawer from "pages/NewFinance/Drawer";
import FinanceService from "Lib/API/Finance";
import Dialog from "@material-ui/core/Dialog";
import ViewDocumentModal from "pages/NewFinance/Documents/ViewDocumentModal";
import { fontConfig } from "theme/fontConfig";

const TableCellHead = withStyles(() => ({
  root: {
    height: 39,
    padding: "0px 20px",
    borderBottom: "none",
    fontFamily: fontConfig.GeneralTableCellHeadFont.family,
    fontWeight: 700,
    fontSize: 12,
    color: "#4d5565",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
}))(TableCell);

const TableCellBody = withStyles(() => ({
  root: {
    height: 56,
    padding: "0px 20px",
    fontFamily: fontConfig.GeneralTableCellBodyFont.family,
    fontSize: 14,
    color: "#4f4f4f",
    borderBottom: "1px solid rgba(224,224,224,0.5)",
  },
}))(TableCell);

const SummaryBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
`;
const SummaryCard = styled.div`
  background: #f3f6f9;
  border-radius: 10px;
  padding: 16px 20px;
  text-align: center;
`;
const SummaryValue = styled.div`
  font-family: var(--FundraisingSummaryValueFont-family);
  font-size: 20px;
  font-weight: 700;
  color: #2555ef;
  margin-bottom: 2px;
`;
const SummaryLabel = styled.div`
  font-family: var(--FundraisingSummaryLabelFont-family);
  font-size: 11px;
  font-weight: 500;
  color: #4d5565;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;
const Badge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  font-family: var(--FundraisingBadgeFont-family);
  background: ${({ variant }) =>
    variant === "pending" ? "rgba(245,158,11,0.12)" :
    variant === "yes"     ? "rgba(37,85,239,0.12)"  : "#f3f6f9"};
  color: ${({ variant }) =>
    variant === "pending" ? "#d97706" :
    variant === "yes"     ? "#2555ef" : "#8e8e8e"};
`;

const useStyles = makeStyles({
  clickableRow: {
    cursor: "pointer",
    "&:hover": { backgroundColor: "#f3f6f9" },
  },
});

const ANON_NAMES = ["anonymous", "anonymous donor"];
const isAnonymous = (d) =>
  d.anonymous || ANON_NAMES.includes((d.name || "").toLowerCase().trim());

const HEADERS = ["#", "Donor", "Amount", "Date", "Status", "Anonymous"];

const DonorsList = ({ donors, campaignId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { donors: list = [], total = 0, page = 1, limit = 20 } = donors || {};

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  const [letterContent, setLetterContent] = useState(null);
  const [viewDocumentId, setViewDocumentId] = useState(null);

  const handleRowClick = async (d) => {
    const token = await getAccessTokenSilently();
    try {
      const res = await FinanceService.getTransactionById(token, d._id);
      const tx = res?.result ?? res;
      // The drawer expects `charge` (single object) — the list pipeline unwinds Charges[] to charge
      const charge = tx?.Charges?.[0] ?? {};
      setDrawerData({ ...tx, charge, transactionId: tx?._id });
      setDrawerOpen(true);
    } catch {
      setDrawerData({ ...d, charge_type: "donation", transactionId: d._id });
      setDrawerOpen(true);
    }
  };

  const handleDownloadFile = async (row) => {
    if (!row?._id) return;
    const token = await getAccessTokenSilently();
    const response = await FinanceService.downloadDocument(token, row._id);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${row.type || "document"}_${row._id}.pdf`);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePageChange = async (_, newPage) => {
    const token = await getAccessTokenSilently();
    dispatch(fetchDonors({ id: campaignId, token, page: newPage }));
  };

  if (!list.length) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", fontFamily: fontConfig.FundraisingDonorsListTextFont.family, color: "#9ca3af", fontSize: 14 }}>
        No donations yet.
      </div>
    );
  }

  const paidList    = list.filter(d => !d.pending);
  const pendingList = list.filter(d => d.pending);
  const totalRaised  = paidList.reduce((sum, d) => sum + (d.amount || 0), 0);
  const totalPending = pendingList.reduce((sum, d) => sum + (d.amount || 0), 0);
  const avgDonation  = paidList.length ? totalRaised / paidList.length : 0;
  const pageCount = Math.ceil(total / limit);
  const rowOffset = (page - 1) * limit;

  return (
    <div>
      <SummaryBar>
        <SummaryCard>
          <SummaryValue>{total}</SummaryValue>
          <SummaryLabel>Total Donors</SummaryLabel>
        </SummaryCard>
        <SummaryCard>
          <SummaryValue>{numberFormat({ number: totalRaised, decimal: 0, style: "currency" })}</SummaryValue>
          <SummaryLabel>Paid This Page</SummaryLabel>
        </SummaryCard>
        <SummaryCard style={totalPending > 0 ? { background: "rgba(245,158,11,0.1)" } : {}}>
          <SummaryValue style={totalPending > 0 ? { color: "#d97706" } : {}}>
            {numberFormat({ number: totalPending, decimal: 0, style: "currency" })}
          </SummaryValue>
          <SummaryLabel>Awaiting Payment</SummaryLabel>
        </SummaryCard>
        <SummaryCard>
          <SummaryValue>{numberFormat({ number: avgDonation, decimal: 0, style: "currency" })}</SummaryValue>
          <SummaryLabel>Avg Donation</SummaryLabel>
        </SummaryCard>
      </SummaryBar>

      <TableContainer>
        <Table style={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead style={{ backgroundColor: "#f3f6f9" }}>
            <TableRow>
              <TableCellHead style={{ width: 52 }}>#</TableCellHead>
              <TableCellHead>Donor</TableCellHead>
              <TableCellHead style={{ width: 130 }}>Amount</TableCellHead>
              <TableCellHead style={{ width: 140 }}>Date</TableCellHead>
              <TableCellHead style={{ width: 110 }}>Status</TableCellHead>
              <TableCellHead style={{ width: 120 }}>Anonymous</TableCellHead>
            </TableRow>
          </TableHead>
          <TableBody style={{ backgroundColor: "#fff" }}>
            {list.map((d, idx) => {
              const anon = isAnonymous(d);
              return (
                <TableRow key={d._id} className={classes.clickableRow} onClick={() => handleRowClick(d)}>
                  <TableCellBody style={{ color: "#9ca3af", fontSize: 12 }}>
                    {rowOffset + idx + 1}
                  </TableCellBody>
                  <TableCellBody>
                    <span style={{ fontWeight: 500, color: "#002e5c" }}>
                      {d.name || "—"}
                    </span>
                  </TableCellBody>
                  <TableCellBody style={{ color: "#4f4f4f", fontWeight: 500 }}>
                    {numberFormat({ number: d.amount, decimal: 0, style: "currency" })}
                  </TableCellBody>
                  <TableCellBody>
                    {d.date ? formatDate({ date: d.date, formatType: "default" }) : "—"}
                  </TableCellBody>
                  <TableCellBody>
                    <Badge variant={d.pending ? "pending" : "paid"}>
                      {d.pending ? "Pending" : "Paid"}
                    </Badge>
                  </TableCellBody>
                  <TableCellBody>
                    <Badge variant={anon ? "yes" : "no"}>{anon ? "Yes" : "No"}</Badge>
                  </TableCellBody>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {pageCount > 1 && (
        <div className="pagination-custom mt-9">
          <Pagination
            count={pageCount}
            color="primary"
            variant="outlined"
            page={page}
            onChange={handlePageChange}
          />
        </div>
      )}

      <RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {drawerData && (
          <FinanceDrawer
            data={drawerData}
            handleClose={() => setDrawerOpen(false)}
            onReversalClick={() => {}}
            viewDocumentHandler={(id) => setViewDocumentId(id)}
            downloadFileHandler={handleDownloadFile}
            documentHandler={() => {}}
            onSharePaymentLinkClick={() => {}}
            viewLetterHandler={(content) => setLetterContent(content)}
          />
        )}
      </RightDrawer>

      <Dialog
        open={viewDocumentId !== null || letterContent !== null}
        onClose={() => { setViewDocumentId(null); setLetterContent(null); }}
        PaperProps={{ style: { width: "888px", height: "90vh", overflow: "hidden", borderRadius: "22px" } }}
        fullWidth
        maxWidth="md"
      >
        <ViewDocumentModal
          documentId={viewDocumentId ?? undefined}
          documentHtmlContent={letterContent ?? undefined}
          closeModal={() => { setViewDocumentId(null); setLetterContent(null); }}
        />
      </Dialog>
    </div>
  );
};

export default DonorsList;
